import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiMinus, FiPlus, FiTrash2, FiShoppingBag } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCart } from '../context/CartContext';

const CartDrawer = () => {
  const { cart, cartTotal, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, setIsCheckingOut } = useCart();

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-zinc-950 border-l border-white/10 z-[201] flex flex-col"
          >
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiShoppingBag} className="text-xl text-fuchsia-500" />
                <h2 className="text-lg font-black uppercase tracking-tighter">Your Arsenal</h2>
              </div>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-white/5 rounded-full transition-colors"
                aria-label="Close cart"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6 hide-scrollbar">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
                    <SafeIcon icon={FiShoppingBag} className="text-2xl text-zinc-700" />
                  </div>
                  <p className="text-zinc-500 font-bold uppercase tracking-widest text-xs">Inventory Empty</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="mt-6 text-fuchsia-500 text-[10px] font-black uppercase tracking-[0.2em] hover:text-white transition-colors"
                  >
                    Return to Drop
                  </button>
                </div>
              ) : (
                cart.map((item) => (
                  <div key={item.id} className="flex gap-4 group">
                    <div className="w-20 h-24 bg-zinc-900 rounded-lg overflow-hidden flex-shrink-0 border border-white/5">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <h3 className="text-sm font-black uppercase tracking-tight text-white">{item.name}</h3>
                        <button onClick={() => removeFromCart(item.id)} className="text-zinc-600 hover:text-red-500 transition-colors">
                          <SafeIcon icon={FiTrash2} className="text-sm" />
                        </button>
                      </div>
                      <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-4">{item.device}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border border-white/10 rounded-md">
                          <button onClick={() => updateQuantity(item.id, -1)} className="p-1.5 hover:bg-white/5 text-zinc-400"><SafeIcon icon={FiMinus} /></button>
                          <span className="w-8 text-center text-xs font-mono font-bold">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, 1)} className="p-1.5 hover:bg-white/5 text-zinc-400"><SafeIcon icon={FiPlus} /></button>
                        </div>
                        <p className="font-mono font-bold text-sm text-cyan-400">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t border-white/5 bg-zinc-900/50">
                <div className="flex justify-between mb-6">
                  <span className="text-zinc-500 text-xs font-black uppercase tracking-[0.2em]">Total Credit</span>
                  <span className="text-xl font-black font-mono text-white">${cartTotal.toFixed(2)}</span>
                </div>
                <button 
                  onClick={() => {
                    setIsCartOpen(false);
                    setIsCheckingOut(true);
                  }}
                  className="w-full h-14 bg-white text-black font-black uppercase tracking-[0.2em] text-xs hover:bg-fuchsia-500 hover:text-white transition-all shadow-[0_0_30px_rgba(255,255,255,0.1)] active:scale-[0.98]"
                >
                  Initiate Checkout
                </button>
                <p className="text-[9px] text-center text-zinc-600 mt-4 uppercase tracking-widest">Secure encrypted transaction protocol</p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;